import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "./i18n";
import { isMissingAssetError, reloadForMissingAsset, stripReloadQuery } from "./utils/clientReload";

stripReloadQuery();

// Suppress ResizeObserver loop error - this is a known browser issue that's harmless
// https://github.com/WICG/resize-observer/issues/38
const resizeObserverErrDiv = document.getElementById(
  "webpack-dev-server-client-overlay-div"
);
const resizeObserverErr = document.getElementById(
  "webpack-dev-server-client-overlay"
);

// Check if message is a ResizeObserver error
const isResizeObserverError = (msg) => {
  return (
    msg?.includes?.("ResizeObserver loop") ||
    msg?.includes?.("ResizeObserver loop completed") ||
    msg?.includes?.("ResizeObserver loop limit exceeded")
  );
};

// Handle error events with capture phase (fires before React's handler)
window.addEventListener(
  "error",
  (e) => {
    if (isResizeObserverError(e?.message) || isResizeObserverError(e?.error?.message)) {
      e.stopImmediatePropagation();
      e.stopPropagation();
      e.preventDefault();
      // Hide any error overlay that might have appeared
      if (resizeObserverErrDiv) resizeObserverErrDiv.style.display = "none";
      if (resizeObserverErr) resizeObserverErr.style.display = "none";
      return true;
    }
    const targetSrc = e?.target?.src || e?.target?.href || "";
    if (
      isMissingAssetError(e?.message) ||
      /\/static\/(js|css)\//.test(String(targetSrc))
    ) {
      reloadForMissingAsset();
    }
  },
  true // Use capture phase
);

// Handle unhandled rejections
window.addEventListener(
  "unhandledrejection",
  (e) => {
    const msg = e?.reason?.message || String(e?.reason) || "";
    if (isResizeObserverError(msg)) {
      e.stopImmediatePropagation();
      e.stopPropagation();
      e.preventDefault();
      return true;
    }
    if (isMissingAssetError(msg)) {
      e.preventDefault();
      reloadForMissingAsset();
    }
  },
  true // Use capture phase
);

// Override console.error to suppress ResizeObserver warnings
const originalConsoleError = console.error;
const isKnownBenignDevWarning = (...args) => {
  const s = args.map((arg) => String(arg)).join(" ");
  return (
    (s.includes("AuthProvider") && s.includes("defaultProps")) ||
    s.includes("React Router Future Flag Warning")
  );
};

console.error = (...args) => {
  if (
    args.some((arg) => isResizeObserverError(String(arg))) ||
    isKnownBenignDevWarning(...args)
  ) {
    return;
  }
  originalConsoleError.apply(console, args);
};

const originalConsoleWarn = console.warn;
console.warn = (...args) => {
  if (isKnownBenignDevWarning(...args)) {
    return;
  }
  originalConsoleWarn.apply(console, args);
};

// MutationObserver to hide React error overlay when ResizeObserver error appears
const hideResizeObserverOverlay = () => {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          // Check for error overlay elements
          const overlays = document.querySelectorAll(
            'iframe[style*="position: fixed"], div[style*="position: fixed"][style*="z-index"]'
          );
          overlays.forEach((overlay) => {
            const content = overlay.textContent || overlay.contentDocument?.body?.textContent || "";
            if (isResizeObserverError(content)) {
              overlay.style.display = "none";
              overlay.remove();
            }
          });
        }
      });
    });
  });

  observer.observe(document.body || document.documentElement, {
    childList: true,
    subtree: true,
  });
};

// Start observing after DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", hideResizeObserverOverlay);
} else {
  hideResizeObserverOverlay();
}

// Add this function to set the viewport
function setViewport() {
  const viewport = document.querySelector('meta[name="viewport"]');
  if (viewport) {
    viewport.content = "width=1500";
  } else {
    const newViewport = document.createElement("meta");
    newViewport.name = "viewport";
    newViewport.content = "width=1024";
    document.head.appendChild(newViewport);
  }
}
// Call the function before rendering
setViewport();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <BrowserRouter
    future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
  >
    <App />
  </BrowserRouter>
);
